import { db, getCollectionPath } from '../lib/firebase';
import { doc, getDoc, updateDoc, setDoc } from 'firebase/firestore';
import { Match, Tournament } from '../types';

export class TournamentService {
  /**
   * Processes the result of a tournament match, updating zone standings
   * and advancing teams in elimination brackets.
   */
  static async updateTournamentAfterMatch(
    match: Match,
    finalS1: string,
    finalS2: string,
    finalS3: string,
    winnerId: string
  ): Promise<void> {
    if (!match.tournamentId) return;

    try {
      const tournamentRef = doc(db, getCollectionPath('tournaments'), match.tournamentId);
      const tSnap = await getDoc(tournamentRef);
      if (!tSnap.exists()) return;
      
      const tData = tSnap.data() as Tournament;

      // 1. Update Zone Standings
      if (match.zoneIndex !== undefined) {
        const zones = [...(tData.zones || [])];
        const zone = zones[match.zoneIndex];
        
        if (zone && zone.teams) {
          const updatedTeams = zone.teams.map((zt: any) => {
            if (zt.teamId === match.team1Id || zt.teamId === match.team2Id) {
              const isTeam1 = zt.teamId === match.team1Id;
              const isWinner = zt.teamId === winnerId;
              
              let setsWon = 0; 
              let setsLost = 0;
              
              [finalS1, finalS2, finalS3].filter(Boolean).forEach(s => {
                const [vs1, vs2] = s.split('-').map(Number);
                if (!isNaN(vs1) && !isNaN(vs2)) {
                  if (isTeam1) { 
                    if (vs1 > vs2) setsWon++; 
                    else if (vs2 > vs1) setsLost++; 
                  } else { 
                    if (vs2 > vs1) setsWon++; 
                    else if (vs1 > vs2) setsLost++; 
                  }
                }
              });
              
              return { 
                ...zt, 
                pj: (zt.pj || 0) + 1, 
                pg: (zt.pg || 0) + (isWinner ? 1 : 0), 
                pts: (zt.pts || 0) + (isWinner ? 3 : 1), 
                sf: (zt.sf || 0) + setsWon, 
                sc: (zt.sc || 0) + setsLost 
              };
            }
            return zt;
          });
          
          zones[match.zoneIndex] = { ...zone, teams: updatedTeams };
          await updateDoc(tournamentRef, { zones });
        }
      } 
      // 2. Update Bracket Advancement
      else if (match.id.startsWith(`bracket_${match.tournamentId}_`)) {
        const matchRegex = match.id.match(/_(\d+)_(\d+)$/);
        
        if (matchRegex && tData.brackets) {
          const bIdx = parseInt(matchRegex[1], 10);
          const mIdx = parseInt(matchRegex[2], 10);
          
          if (tData.brackets[bIdx] && tData.brackets[bIdx].matches[mIdx]) {
            const nb = [...tData.brackets];
            nb[bIdx].matches[mIdx] = {
              ...nb[bIdx].matches[mIdx],
              s1: finalS1, 
              s2: finalS2, 
              s3: finalS3, 
              winnerId
            };
            
            const nextBIdx = bIdx + 1;
            const nextMIdx = Math.floor(mIdx / 2);
            const isTeam1Slot = mIdx % 2 === 0;

            if (nb[nextBIdx] && nb[nextBIdx].matches[nextMIdx]) {
              const nextMatch = nb[nextBIdx].matches[nextMIdx];
              
              if (winnerId) {
                if (isTeam1Slot) nextMatch.team1Id = winnerId;
                else nextMatch.team2Id = winnerId;
              }
              
              const nextMatchId = `bracket_${match.tournamentId}_${nextBIdx}_${nextMIdx}`;
              
              let isNextPublished = nextMatch.isPublished;
              if (!isNextPublished && nextMatch.team1Id && nextMatch.team2Id && nextMatch.team1Id !== 'BYE' && nextMatch.team2Id !== 'BYE') {
                 nextMatch.isPublished = true;
                 await setDoc(doc(db, getCollectionPath('matches'), nextMatchId), {
                   tournamentId: match.tournamentId,
                   tournamentName: tData.name || '',
                   round: tData.brackets[nextBIdx]?.round || 'Fase Final',
                   team1Id: nextMatch.team1Id,
                   team2Id: nextMatch.team2Id,
                   status: 'pending',
                   organizerId: tData.organizerId || '',
                   createdAt: Date.now(),
                 }, { merge: true });
              } else if (isNextPublished) {
                 await setDoc(doc(db, getCollectionPath('matches'), nextMatchId), {
                   team1Id: nextMatch.team1Id || '',
                   team2Id: nextMatch.team2Id || '',
                   status: 'pending'
                 }, { merge: true });
              }
            }
            
            // Third Place Match Logic
            let updatedThirdPlaceMatch = tData.thirdPlaceMatch;
            if (nextBIdx === nb.length - 1) {
              const loserId = winnerId ? (winnerId === nb[bIdx].matches[mIdx].team1Id ? nb[bIdx].matches[mIdx].team2Id : nb[bIdx].matches[mIdx].team1Id) : '';
              
              if (!updatedThirdPlaceMatch) {
                updatedThirdPlaceMatch = {
                  id: `bracket_${match.tournamentId}_999_0`,
                  team1Id: '',
                  team2Id: '',
                  winnerId: '',
                  s1: '', s2: '', s3: '', score: '',
                  isPublished: false
                };
              }

              if (mIdx === 0) {
                updatedThirdPlaceMatch.team1Id = loserId || '';
              } else if (mIdx === 1) {
                updatedThirdPlaceMatch.team2Id = loserId || '';
              }
              
              const thirdPlaceMatchId = `bracket_${match.tournamentId}_999_0`;
              let isThirdPublished = updatedThirdPlaceMatch.isPublished;
              
              if (!isThirdPublished && updatedThirdPlaceMatch.team1Id && updatedThirdPlaceMatch.team2Id && updatedThirdPlaceMatch.team1Id !== 'BYE' && updatedThirdPlaceMatch.team2Id !== 'BYE') {
                 updatedThirdPlaceMatch.isPublished = true;
                 await setDoc(doc(db, getCollectionPath('matches'), thirdPlaceMatchId), {
                   tournamentId: match.tournamentId,
                   tournamentName: tData.name || '',
                   round: '3er Puesto',
                   team1Id: updatedThirdPlaceMatch.team1Id,
                   team2Id: updatedThirdPlaceMatch.team2Id,
                   status: 'pending',
                   organizerId: tData.organizerId || '',
                   createdAt: Date.now(),
                 }, { merge: true });
              } else if (isThirdPublished) {
                 await setDoc(doc(db, getCollectionPath('matches'), thirdPlaceMatchId), {
                   team1Id: updatedThirdPlaceMatch.team1Id || '',
                   team2Id: updatedThirdPlaceMatch.team2Id || '',
                   status: 'pending'
                 }, { merge: true });
              }
            }
            
            const updatePayload: any = { brackets: nb };
            if (nextBIdx === nb.length - 1 && updatedThirdPlaceMatch) {
              updatePayload.thirdPlaceMatch = updatedThirdPlaceMatch;
            }
            await updateDoc(tournamentRef, updatePayload);
          }
        }
      }
    } catch (error) {
      console.error(`Error in TournamentService for match ${match.id}:`, error);
    }
  }
}
